/**
 * BrandPanel — the right-side aside on every onboarding screen.
 *
 * Prototype's `.brand-col-inner`. Wraps the eyebrow + h2 + subtitle + slots
 * for InsightPanel, StoryList, and a footer source line. Each onboarding route
 * passes its own copy.
 *
 * The brand column itself (the dot-tile background) is set up by `OnboardingShell`;
 * this component is what goes inside.
 */
import * as React from "react";
import { cn } from "@flint/ui";

export interface BrandPanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Small eyebrow above the title. */
  eyebrow?: React.ReactNode;
  /** Pull quote / h2. Drives the screen's narrative. */
  title: React.ReactNode;
  /** Supporting paragraph beneath the title. */
  subtitle?: React.ReactNode;
  /** Source / attribution strip at the bottom — small, dot-prefixed text. */
  source?: React.ReactNode;
}

export function BrandPanel({
  eyebrow,
  title,
  subtitle,
  source,
  children,
  className,
  ...props
}: BrandPanelProps) {
  return (
    <div className={cn("flex flex-col gap-6 max-w-[440px]", className)} {...props}>
      {eyebrow ? (
        <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted m-0">
          {eyebrow}
        </p>
      ) : null}
      <h2 className="text-2xl leading-[30px] font-semibold tracking-[-0.01em] text-ink m-0">
        {title}
      </h2>
      {subtitle ? <p className="text-sm leading-[22px] text-muted m-0">{subtitle}</p> : null}

      <div className="flex flex-col gap-6">{children}</div>

      {source ? (
        <div className="inline-flex items-center gap-1.5 text-xs text-muted mt-2">
          <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-sky-600" />
          {source}
        </div>
      ) : null}
    </div>
  );
}
